"use client";

import type { CreateProfileFormData } from "@/features/profile/tutor/schemas/createProfileSchema";
import type {
  GroupedSubject,
  Subject,
  SubjectResponse,
} from "@/features/profile/tutor/types/subjects";
import type React from "react";

import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { type Control, useFieldArray, useWatch } from "react-hook-form";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useDebounce } from "use-debounce";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { CoachMarker } from "@/components/CoachMarker";

interface SelectSubjectsStepProps {
  control: Control<CreateProfileFormData>;
  setNavButtonsDisabled: Dispatch<SetStateAction<boolean>>;
}

// Enum for selection steps
enum SelectionStep {
  PARENT_SUBJECTS = 0,
  CHILD_SUBJECTS = 1,
  CURRICULUM_SELECTION = 2,
  CHILD_CURRICULUM_SELECTION = 3,
}

export function SelectSubjects({ control, setNavButtonsDisabled }: SelectSubjectsStepProps) {
  const { t } = useTranslation(["profile", "common"]);
  const [subjects, setSubjects] = useState<(Subject | GroupedSubject)[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [childSearch, setChildSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const [currentStep, setCurrentStep] = useState<SelectionStep>(SelectionStep.PARENT_SUBJECTS);
  const [selectedParentSubject, setSelectedParentSubject] = useState<GroupedSubject | null>(null);
  const [selectedChildSubject, setSelectedChildSubject] = useState<Subject | null>(null);
  const [selectedParentCurriculumLevels, setSelectedParentCurriculumLevels] = useState<string[]>(
    [],
  );
  const [selectedChildCurriculumLevels, setSelectedChildCurriculumLevels] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<(Subject | GroupedSubject)[]>([]);
  const [direction, setDirection] = useState(0);
  const [isFromSearch, setIsFromSearch] = useState(false);
  const [preventNavOnClear, setPreventNavOnClear] = useState(false);

  const formValues = useWatch({ control });
  const subjectsForm = useWatch({ control, name: "subjects" }) || [];
  const { append, remove } = useFieldArray({
    control,
    name: "subjects",
  });

  useEffect(() => {
    if (
      currentStep === SelectionStep.PARENT_SUBJECTS ||
      currentStep === SelectionStep.CHILD_SUBJECTS
    ) {
      setNavButtonsDisabled(false);
    } else {
      setNavButtonsDisabled(true);
    }
  }, [currentStep]);

  // Modern, subtle animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
  };

  // Page transitions
  const pageTransitionVariants = {
    initial: () => ({
      opacity: 0,
      transition: { duration: 0.15, ease: "easeIn" },
    }),
    animate: {
      opacity: 1,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    exit: () => ({
      opacity: 0,
      transition: { duration: 0.15, ease: "easeIn" },
    }),
  };

  // Staggered item animations
  const listItemVariants = {
    hidden: { opacity: 0, y: 5 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut",
        delay: i * 0.03,
      },
    }),
  };

  // Header animation
  const headerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: { duration: 0.2, ease: "easeOut" },
    },
  };

  useEffect(() => {
    if (debouncedSearch.length >= 3) {
      getSubjects();
      setDirection(1);
    } else if (debouncedSearch.length === 0 && !preventNavOnClear) {
      getSubjects();
      setDirection(-1);
    }
    setPreventNavOnClear(false);
  }, [debouncedSearch]);

  async function getSubjects() {
    try {
      setIsLoading(true);
      const { data } = await axiosApiInstance.get<SubjectResponse>(
        API_ENDPOINTS.TUTOR.UTILS.SUBJECTS,
        {
          params: { locale_code: formValues.locale_code, search: debouncedSearch },
        },
      );

      setSubjects(data.data);

      // Store search results separately if we're searching
      if (debouncedSearch.length >= 3) {
        setSearchResults(data.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  // Helper function to get curriculum levels for a subject
  const getCurriculumLevelsForSubject = (subjectId: string) => {
    const subject = subjects.find((s) => s.id === subjectId);

    if (subject && "curriculum_levels" in subject) {
      return subject.curriculum_levels || [];
    }

    return [];
  };

  // Helper function to get curriculum levels for a child subject (inherits from parent)
  const getCurriculumLevelsForChildSubject = () => {
    if (!selectedParentSubject) return [];

    // Child subjects inherit curriculum levels from their parent
    return selectedParentSubject.curriculum_levels || [];
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setSearch(value);
  };

  const handleChildSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setChildSearch(value);
  };

  const clearChildSearch = () => {
    setChildSearch("");
  };

  const clearSearch = () => {
    setPreventNavOnClear(true);
    setSearch("");
  };

  const handleParentSubjectClick = (subject: GroupedSubject) => {
    setSelectedParentSubject(subject);

    // Check if the subject is already selected
    const existingSubject = subjectsForm.find((s) => s.id === subject.id);

    if (existingSubject) {
      // If already selected, set curriculum levels and proceed to child subjects
      setSelectedParentCurriculumLevels(existingSubject.curriculum_levels || []);
      setDirection(1); // Forward direction
      setCurrentStep(SelectionStep.CHILD_SUBJECTS);
    } else {
      // If not selected, first go to curriculum selection step
      setSelectedParentCurriculumLevels([]);
      setDirection(1); // Forward direction
      setCurrentStep(SelectionStep.CURRICULUM_SELECTION);
    }
  };

  const handleCurriculumLevelToggle = (levelId: string, isParent = true) => {
    if (isParent) {
      if (selectedParentCurriculumLevels.includes(levelId)) {
        setSelectedParentCurriculumLevels(
          selectedParentCurriculumLevels.filter((id) => id !== levelId),
        );
      } else {
        setSelectedParentCurriculumLevels([...selectedParentCurriculumLevels, levelId]);
      }
    } else {
      if (selectedChildCurriculumLevels.includes(levelId)) {
        setSelectedChildCurriculumLevels(
          selectedChildCurriculumLevels.filter((id) => id !== levelId),
        );
      } else {
        setSelectedChildCurriculumLevels([...selectedChildCurriculumLevels, levelId]);
      }
    }
  };

  const handleCurriculumLevelConfirm = () => {
    if (selectedParentSubject) {
      const existingIndex = subjectsForm.findIndex((s) => s.id === selectedParentSubject.id);

      // If no curriculum levels are selected, don't add the subject
      if (selectedParentCurriculumLevels.length === 0) {
        return;
      }

      if (existingIndex !== -1) {
        remove(existingIndex);
      }

      append({
        id: selectedParentSubject.id,
        curriculum_levels: selectedParentCurriculumLevels,
      });

      // If coming from search, go back to parent subjects step
      if (isFromSearch) {
        setIsFromSearch(false);
        setDirection(-1);
        setCurrentStep(SelectionStep.PARENT_SUBJECTS);
        setSelectedParentSubject(null);
      } else {
        // Otherwise, proceed to child subjects step
        setDirection(1);
        setCurrentStep(SelectionStep.CHILD_SUBJECTS);
      }
    }
  };

  const handleChildSubjectToggle = (subject: Subject) => {
    const existingSubject = subjectsForm.find((s) => s.id === subject.id);

    if (existingSubject) {
      // Remove the subject if it's already selected
      const existingIndex = subjectsForm.findIndex((s) => s.id === subject.id);

      if (existingIndex !== -1) {
        remove(existingIndex);
      }
    } else {
      // Add the subject with parent's curriculum levels
      append({
        id: subject.id,
        curriculum_levels: selectedParentCurriculumLevels,
      });
    }
  };

  const handleEditChildCurriculumLevels = (subject: Subject) => {
    setSelectedChildSubject(subject);
    const existingSubject = subjectsForm.find((s) => s.id === subject.id);

    setSelectedChildCurriculumLevels(existingSubject?.curriculum_levels || []);
    setDirection(1);
    setCurrentStep(SelectionStep.CHILD_CURRICULUM_SELECTION);
  };

  const handleChildCurriculumLevelConfirm = () => {
    if (selectedChildSubject) {
      const existingIndex = subjectsForm.findIndex((s) => s.id === selectedChildSubject.id);

      // If no curriculum levels are selected, remove the subject
      if (selectedChildCurriculumLevels.length === 0) {
        if (existingIndex !== -1) {
          remove(existingIndex);
        }
      } else {
        if (existingIndex !== -1) {
          remove(existingIndex);
        }

        append({
          id: selectedChildSubject.id,
          curriculum_levels: selectedChildCurriculumLevels,
        });
      }

      // Go back to child subjects step
      setDirection(-1);
      setCurrentStep(SelectionStep.CHILD_SUBJECTS);
    }
  };

  const navigateBack = () => {
    setDirection(-1); // Backward direction
    setChildSearch(""); // Clear child search

    if (currentStep === SelectionStep.CURRICULUM_SELECTION) {
      setCurrentStep(SelectionStep.PARENT_SUBJECTS);
      setSelectedParentSubject(null);
    } else if (currentStep === SelectionStep.CHILD_SUBJECTS) {
      setCurrentStep(SelectionStep.PARENT_SUBJECTS);
      setSelectedParentSubject(null);
    } else if (currentStep === SelectionStep.CHILD_CURRICULUM_SELECTION) {
      setCurrentStep(SelectionStep.CHILD_SUBJECTS);
      setSelectedChildSubject(null);
    }
  };

  const handleSearchResultClick = (subject: Subject | GroupedSubject) => {
    setIsFromSearch(true);
    if ("is_group_subject" in subject && subject.is_group_subject) {
      // It's a group subject
      // Fetch the full subject with children if needed
      const fullSubject = subjects.find((s) => s.id === subject.id) as GroupedSubject;

      if (fullSubject && "children" in fullSubject) {
        setSelectedParentSubject(fullSubject);
      } else {
        // If not found in the main subjects list, create a GroupedSubject with empty children
        setSelectedParentSubject({
          ...subject,
          children: [],
        } as GroupedSubject);
      }
    } else {
      // It's a regular subject, create a GroupedSubject-like object with empty children
      setSelectedParentSubject({
        ...subject,
        children: [],
        is_group_subject: false,
      } as GroupedSubject);
    }

    // Check if already selected
    const existingSubject = subjectsForm.find((s) => s.id === subject.id);

    if (existingSubject) {
      setSelectedParentCurriculumLevels(existingSubject.curriculum_levels || []);
      setDirection(1);
      setCurrentStep(SelectionStep.CURRICULUM_SELECTION);
    } else {
      setSelectedParentCurriculumLevels([]);
      setDirection(1);
      setCurrentStep(SelectionStep.CURRICULUM_SELECTION);
    }
  };

  const isSubjectSelected = (id: string) => subjectsForm.some((subject) => subject.id === id);

  const handleUnselectParentSubject = (e: React.MouseEvent, subjectId: string) => {
    e.stopPropagation();
    const existingIndex = subjectsForm.findIndex((s) => s.id === subjectId);

    if (existingIndex !== -1) {
      // Also remove any child subjects that were selected
      const childSubjects =
        subjects
          .filter((subject): subject is GroupedSubject => "children" in subject)
          .find((subject) => subject.id === subjectId)?.children || [];

      const childIds = childSubjects.map((child) => child.id);

      // Remove the parent subject
      remove(existingIndex);

      // Remove any selected child subjects
      childIds.forEach((childId) => {
        const childIndex = subjectsForm.findIndex((s) => s.id === childId);

        if (childIndex !== -1) {
          remove(childIndex);
        }
      });
    }
  };

  const handleEditParentCurriculumLevels = (e: React.MouseEvent, subject: GroupedSubject) => {
    e.stopPropagation();
    setSelectedParentSubject(subject);
    const existingSubject = subjectsForm.find((s) => s.id === subject.id);

    setSelectedParentCurriculumLevels(existingSubject?.curriculum_levels || []);
    setDirection(1);
    setCurrentStep(SelectionStep.CURRICULUM_SELECTION);
  };

  const getSelectedCurriculumLevelsNames = (subjectId: string) => {
    const selectedSubject = subjectsForm.find((s) => s.id === subjectId);

    if (!selectedSubject) return "";

    // Resolve the list of available curriculum levels (with names) for this subjectId
    const resolveLevelsForSubject = (): { id: string; name: string }[] => {
      // 1) If this subject exists in the fetched subjects and has its own levels, use them
      const subjectFromAll = subjects.find((s) => s.id === subjectId);

      if (subjectFromAll && "curriculum_levels" in subjectFromAll) {
        return subjectFromAll.curriculum_levels || [];
      }

      // 2) If it's a child of the currently selected parent subject, use parent's levels
      if (selectedParentSubject?.children?.some((c) => c.id === subjectId)) {
        return selectedParentSubject.curriculum_levels || [];
      }

      // 3) Fallback: find any group subject in the list that contains this child id
      const group = subjects
        .filter((s): s is GroupedSubject => "children" in s)
        .find((g) => g.children?.some((c) => c.id === subjectId));

      if (group) {
        return group.curriculum_levels || [];
      }

      return [];
    };

    const availableLevels = resolveLevelsForSubject();

    return selectedSubject.curriculum_levels
      .map((levelId) => availableLevels.find((l) => l.id === levelId)?.name || "")
      .filter(Boolean)
      .join(", ");
  };

  // Render parent subject selection
  const renderParentSubjectsStep = () => {
    return (
      <motion.div
        key="parent-subjects"
        animate="animate"
        className="absolute inset-0 mx-auto w-full max-w-3xl px-4"
        custom={direction}
        exit="exit"
        initial="initial"
        variants={pageTransitionVariants}
      >
        <motion.div
          animate={{ opacity: 1 }}
          className="mb-6 text-center"
          initial={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-2 flex items-center justify-center gap-2">
            <div className="bg-accent-foreground size-10 rounded-full p-2">
              <Icon className="text-primary" height={"100%"} icon="tabler:book" width={"100%"} />
            </div>
            <h2 className="text-lg font-semibold sm:text-xl">
              {t("profile:selectSubjects.subjects")}
            </h2>
          </div>
          <p className="text-muted-foreground mx-auto max-w-lg text-sm sm:text-base">
            {t("profile:selectSubjects.subtitle")}
          </p>
        </motion.div>

        <div className="relative mb-4">
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            <Icon className="text-muted-foreground" height={18} icon="tabler:search" width={18} />
          </div>
          <Input
            className="bg-side-accent/50 h-12 pl-10"
            placeholder={t("profile:selectSubjects.searchSubjects")}
            value={search}
            onChange={handleSearchChange}
          />
          {search.length > 0 && (
            <Button
              className="absolute inset-y-0 right-2 my-auto h-8 px-2"
              size="sm"
              variant="ghost"
              onClick={clearSearch}
            >
              <X height={16} width={16} />
            </Button>
          )}
        </div>

        <ScrollArea className="-mx-4 h-[55dvh] px-4 pb-6">
          <div className="space-y-3 pt-2">
            {isLoading ? (
              <div className="flex h-[200px] w-full items-center justify-center">
                <motion.div
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center gap-2"
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="border-sidebar-accent h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
                  <span className="text-muted-foreground">
                    {t("profile:selectSubjects.loadingSubjects")}
                  </span>
                </motion.div>
              </div>
            ) : debouncedSearch.length >= 3 ? (
              // Render search results
              searchResults.length > 0 ? (
                searchResults.map((subject, index) => {
                  const isSelected = isSubjectSelected(subject.id);

                  return (
                    <motion.div
                      key={subject.id}
                      animate="visible"
                      className="p-1"
                      custom={index}
                      initial="hidden"
                      variants={listItemVariants}
                    >
                      <Card
                        className={cn(
                          "mx-auto max-w-[80vw] cursor-pointer overflow-hidden transition-all duration-200",
                          "hover:bg-sidebar-accent border-none",
                          isSelected ? "bg-side-accent" : "bg-muted/50",
                        )}
                        onClick={() => handleSearchResultClick(subject)}
                      >
                        <CardContent className="p-0">
                          <div className="flex flex-wrap items-center justify-between p-3 sm:p-4">
                            <div className="flex min-w-0 flex-1 items-center justify-start gap-2 sm:gap-3">
                              <div className="bg-accent-foreground flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10">
                                <Icon
                                  className="text-primary"
                                  height={16}
                                  icon={subject.icon}
                                  width={16}
                                />
                              </div>
                              <div className="min-w-0 flex-1 text-start">
                                <span className="text-muted-foreground block truncate text-sm font-medium sm:text-base">
                                  {subject.name}
                                </span>
                                {isSelected && (
                                  <div className="text-muted-foreground mt-1 max-w-full truncate text-xs sm:max-w-[85%]">
                                    {getSelectedCurriculumLevelsNames(subject.id)}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2">
                              {isSelected && (
                                <Button
                                  className="text-muted-foreground hover:text-destructive h-6 w-6 sm:h-7 sm:w-7"
                                  size="icon"
                                  title={t("common:edit")}
                                  variant="ghost"
                                  onClick={(e) => handleUnselectParentSubject(e, subject.id)}
                                >
                                  <X height={14} width={14} />
                                </Button>
                              )}
                              <ChevronRight
                                className="text-muted-foreground"
                                height={16}
                                width={16}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })
              ) : (
                <motion.div
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center p-6"
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Icon
                    className="text-muted-foreground mb-2"
                    height={40}
                    icon="tabler:search-off"
                    width={40}
                  />
                  <span className="text-muted-foreground text-center">
                    {t("profile:selectSubjects.noSubjectsAvailable")}
                  </span>
                </motion.div>
              )
            ) : subjects.length > 0 ? (
              // Render regular subjects list
              subjects
                .filter((subject): subject is GroupedSubject => "children" in subject)
                .map((subject, index) => {
                  const isSelected = isSubjectSelected(subject.id);

                  return (
                    <motion.div
                      key={subject.id}
                      animate="visible"
                      className="p-1"
                      custom={index}
                      initial="hidden"
                      variants={listItemVariants}
                    >
                      <Card
                        className={cn(
                          "mx-auto max-w-[80vw] cursor-pointer overflow-hidden transition-all duration-200",
                          "hover:bg-sidebar-accent border-none",
                          isSelected ? "bg-side-accent" : "bg-muted/50",
                        )}
                        onClick={() => handleParentSubjectClick(subject)}
                      >
                        <CardContent className="p-0">
                          <div className="flex flex-wrap items-center justify-between p-3 sm:p-4">
                            <div className="flex min-w-0 flex-1 items-center justify-start gap-2 sm:gap-3">
                              <div className="bg-accent-foreground flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10">
                                <Icon
                                  className="text-primary"
                                  height={16}
                                  icon={subject.icon}
                                  width={16}
                                />
                              </div>
                              <div className="min-w-0 flex-1 text-start">
                                <span className="text-muted-foreground block truncate text-sm font-medium sm:text-base">
                                  {subject.name}
                                </span>
                                {isSelected && (
                                  <div className="text-muted-foreground mt-1 max-w-full truncate text-xs sm:max-w-[85%]">
                                    {getSelectedCurriculumLevelsNames(subject.id)}
                                  </div>
                                )}
                                {(() => {
                                  // Count selected child subjects
                                  const childSubjects = subject.children || [];
                                  const selectedChildCount = childSubjects.filter((child) =>
                                    isSubjectSelected(child.id),
                                  ).length;

                                  if (selectedChildCount > 0) {
                                    return (
                                      <div className="text-muted-foreground mt-1 text-xs">
                                        <Badge
                                          className="px-1.5 py-0 text-xs font-normal"
                                          variant="outline"
                                        >
                                          {t("profile:selectSubjects.childSubjectsSelected", {
                                            count: selectedChildCount,
                                          })}
                                        </Badge>
                                      </div>
                                    );
                                  }

                                  return null;
                                })()}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2">
                              {isSelected && (
                                <Button
                                  className="text-muted-foreground h-6 w-6 sm:h-7 sm:w-7"
                                  size="icon"
                                  title={t("common:edit")}
                                  variant="ghost"
                                  onClick={(e) => handleEditParentCurriculumLevels(e, subject)}
                                >
                                  <Icon height={14} icon="tabler:edit" width={14} />
                                </Button>
                              )}
                              <ChevronRight
                                className="text-muted-foreground"
                                height={16}
                                width={16}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })
            ) : (
              <motion.div
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center p-6"
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Icon
                  className="text-muted-foreground mb-2"
                  height={40}
                  icon="tabler:book-off"
                  width={40}
                />
                <span className="text-muted-foreground text-center">
                  {t("profile:selectSubjects.noSubjectsAvailable")}
                </span>
              </motion.div>
            )}
          </div>
        </ScrollArea>
      </motion.div>
    );
  };

  // Render child subjects selection
  const renderChildSubjectsStep = () => {
    if (!selectedParentSubject) return null;

    return (
      <motion.div
        key="child-subjects"
        animate="animate"
        className="absolute inset-0 mx-auto w-full max-w-3xl px-4"
        custom={direction}
        exit="exit"
        initial="initial"
        variants={pageTransitionVariants}
      >
        <motion.div animate="animate" className="mb-6" initial="initial" variants={headerVariants}>
          <div className="flex flex-col items-center">
            <div className="mb-2 w-full text-left">
              <Button
                className="h-9 rounded-full"
                size="sm"
                variant="outline"
                onClick={navigateBack}
              >
                <ChevronLeft className="mr-1" height={16} width={16} />
                {t("profile:selectSubjects.subjects")}
              </Button>
            </div>

            <h3 className="mb-1 text-xl font-medium">{selectedParentSubject.name}</h3>
            {getSelectedCurriculumLevelsNames(selectedParentSubject.id).length > 0 && (
              <div className="mt-1 flex w-full max-w-full justify-center overflow-hidden text-ellipsis whitespace-nowrap">
                <div className="max-w-[90%] text-sm">
                  <span className="block truncate">
                    {getSelectedCurriculumLevelsNames(selectedParentSubject.id)}
                  </span>
                </div>
              </div>
            )}
            <p className="text-muted-foreground mt-2">
              {t("profile:selectSubjects.selectChildSubjects")}
            </p>
          </div>
        </motion.div>

        <div className="relative mb-4">
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            <Icon className="text-muted-foreground" height={18} icon="tabler:search" width={18} />
          </div>
          <Input
            className="bg-side-accent/50 h-12 pl-10"
            placeholder={t("profile:selectSubjects.searchSubjects")}
            value={childSearch}
            onChange={handleChildSearchChange}
          />
          {childSearch.length > 0 && (
            <Button
              className="absolute inset-y-0 right-2 my-auto h-8 px-2"
              size="sm"
              variant="ghost"
              onClick={clearChildSearch}
            >
              <X height={16} width={16} />
            </Button>
          )}
        </div>

        <ScrollArea className="-mx-4 h-[55dvh] px-4 pb-6 [&_>_div_>_div]:block!">
          <div className="space-y-3 pt-2">
            {/* Parent subject as a selectable option */}
            <motion.div
              animate={{ opacity: 1 }}
              className="p-1"
              initial={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card
                className={cn(
                  "mx-auto max-w-[min(730px,_80vw)] cursor-pointer overflow-hidden transition-all duration-200",
                  "hover:bg-sidebar-accent border-none",
                  isSubjectSelected(selectedParentSubject.id) ? "bg-side-accent" : "bg-muted/50",
                )}
                onClick={() => {
                  // Toggle selection of the parent subject itself
                  const isSelected = isSubjectSelected(selectedParentSubject.id);

                  if (isSelected) {
                    const existingIndex = subjectsForm.findIndex(
                      (s) => s.id === selectedParentSubject.id,
                    );

                    if (existingIndex !== -1) {
                      remove(existingIndex);
                    }
                  } else {
                    append({
                      id: selectedParentSubject.id,
                      curriculum_levels: selectedParentCurriculumLevels,
                    });
                  }
                }}
              >
                <CardContent className="p-0">
                  <div className="flex flex-wrap items-center justify-between p-3 sm:p-4">
                    <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                      <div className="bg-accent-foreground flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10">
                        <Icon
                          className="text-primary"
                          height={16}
                          icon={selectedParentSubject.icon}
                          width={16}
                        />
                      </div>
                      <div className="min-w-0 flex-1 text-start">
                        <div className="text-muted-foreground block truncate text-sm font-medium sm:text-base">
                          {selectedParentSubject.name}
                        </div>
                        {isSubjectSelected(selectedParentSubject.id) && (
                          <div className="text-muted-foreground mt-1 max-w-full truncate text-xs sm:max-w-[85%]">
                            {getSelectedCurriculumLevelsNames(selectedParentSubject.id)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 sm:gap-2">
                      {isSubjectSelected(selectedParentSubject.id) && (
                        <Button
                          className="text-muted-foreground h-6 w-6 sm:h-7 sm:w-7"
                          size="icon"
                          title={t("common:edit")}
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditParentCurriculumLevels(e, selectedParentSubject);
                          }}
                        >
                          <Icon height={14} icon="tabler:edit" width={14} />
                        </Button>
                      )}
                      <div className="flex h-5 w-5 items-center justify-center">
                        {isSubjectSelected(selectedParentSubject.id) && (
                          <motion.div
                            animate={{ scale: 1, opacity: 1 }}
                            initial={{ scale: 0, opacity: 0 }}
                            transition={{ duration: 0.2, type: "spring", stiffness: 500 }}
                          >
                            <Check className="text-accent-foreground" height={16} width={16} />
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Divider between parent and children */}
            <motion.div
              animate={{ opacity: 1 }}
              className="relative py-2"
              initial={{ opacity: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <div className="absolute inset-0 flex items-center">
                <span className="border-border w-full border-t" />
              </div>
            </motion.div>

            {/* Child subjects */}
            {(() => {
              const filteredChildren =
                selectedParentSubject.children?.filter(
                  (child) =>
                    childSearch.length === 0 ||
                    child.name.toLowerCase().includes(childSearch.toLowerCase()),
                ) || [];

              if (filteredChildren.length > 0) {
                return filteredChildren.map((child, index) => {
                  const isSelected = isSubjectSelected(child.id);

                  return (
                    <motion.div
                      key={child.id}
                      animate="visible"
                      className="p-1"
                      custom={index}
                      initial="hidden"
                      variants={listItemVariants}
                    >
                      {index === 0 ? (
                        <CoachMarker
                          description={t(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            "profile:selectSubjects.coachMarks.childIntro.description" as any,
                          )}
                          id="create-profile-select-subjects-child-intro"
                          placement="right"
                          title={t(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            "profile:selectSubjects.coachMarks.childIntro.title" as any,
                          )}
                        >
                          <Card
                            className={cn(
                              "mx-auto max-w-[80vw] cursor-pointer overflow-hidden transition-all duration-200",
                              "hover:bg-sidebar-accent border-none",
                              isSelected ? "bg-side-accent" : "bg-muted/50",
                            )}
                            onClick={() => handleChildSubjectToggle(child)}
                          >
                            <CardContent className="max-w-full p-0">
                              <div className="flex flex-wrap items-center justify-between p-3 sm:p-4">
                                <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                                  <div className="bg-accent-foreground flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full sm:h-9 sm:w-9">
                                    <Icon
                                      className="text-primary"
                                      height={14}
                                      icon={child.icon}
                                      width={14}
                                    />
                                  </div>
                                  <div className="min-w-0 flex-1 text-start">
                                    <div className="text-muted-foreground block truncate text-sm font-medium sm:max-w-[85%]">
                                      {child.name}
                                    </div>
                                    {isSelected && (
                                      <div className="text-muted-foreground max-w-full truncate text-start text-xs sm:max-w-[85%]">
                                        {getSelectedCurriculumLevelsNames(child.id)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 sm:gap-2">
                                  {isSelected && (
                                    <Button
                                      className="h-6 w-6 sm:h-7 sm:w-7"
                                      size="icon"
                                      title={t("common:edit")}
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditChildCurriculumLevels(child);
                                      }}
                                    >
                                      <Icon height={14} icon="tabler:edit" width={14} />
                                    </Button>
                                  )}
                                  <div className="flex h-5 w-5 items-center justify-center">
                                    {isSelected && (
                                      <motion.div
                                        animate={{ scale: 1, opacity: 1 }}
                                        initial={{ scale: 0, opacity: 0 }}
                                        transition={{
                                          duration: 0.2,
                                          type: "spring",
                                          stiffness: 500,
                                        }}
                                      >
                                        <Check
                                          className="text-accent-foreground"
                                          height={14}
                                          width={14}
                                        />
                                      </motion.div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </CoachMarker>
                      ) : (
                        <Card
                          className={cn(
                            "mx-auto max-w-[80vw] cursor-pointer overflow-hidden transition-all duration-200",
                            "hover:bg-sidebar-accent border-none",
                            isSelected ? "bg-side-accent" : "bg-muted/50",
                          )}
                          onClick={() => handleChildSubjectToggle(child)}
                        >
                          <CardContent className="p-0">
                            <div className="flex flex-wrap items-center justify-between p-3 sm:p-4">
                              <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                                <div className="bg-accent-foreground flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full sm:h-9 sm:w-9">
                                  <Icon
                                    className="text-primary"
                                    height={14}
                                    icon={child.icon}
                                    width={14}
                                  />
                                </div>
                                <div className="min-w-0 flex-1 text-start">
                                  <div className="text-muted-foreground block truncate text-sm font-medium sm:max-w-[85%]">
                                    {child.name}
                                  </div>
                                  {isSelected && (
                                    <div className="text-muted-foreground max-w-full truncate text-start text-xs sm:max-w-[85%]">
                                      {getSelectedCurriculumLevelsNames(child.id)}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 sm:gap-2">
                                {isSelected && (
                                  <Button
                                    className="h-6 w-6 sm:h-7 sm:w-7"
                                    size="icon"
                                    title={t("common:edit")}
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditChildCurriculumLevels(child);
                                    }}
                                  >
                                    <Icon height={14} icon="tabler:edit" width={14} />
                                  </Button>
                                )}
                                <div className="flex h-5 w-5 items-center justify-center">
                                  {isSelected && (
                                    <motion.div
                                      animate={{ scale: 1, opacity: 1 }}
                                      initial={{ scale: 0, opacity: 0 }}
                                      transition={{ duration: 0.2, type: "spring", stiffness: 500 }}
                                    >
                                      <Check
                                        className="text-accent-foreground"
                                        height={14}
                                        width={14}
                                      />
                                    </motion.div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </motion.div>
                  );
                });
              } else {
                return (
                  <motion.div
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center py-8"
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Icon
                      className="text-muted-foreground mb-2"
                      height={40}
                      icon={childSearch.length > 0 ? "tabler:search-off" : "tabler:folder-off"}
                      width={40}
                    />
                    <span className="text-muted-foreground text-center">
                      {childSearch.length > 0
                        ? t("profile:selectSubjects.noSubjectsAvailable")
                        : t("profile:selectSubjects.noSubjectsAvailable")}
                    </span>
                  </motion.div>
                );
              }
            })()}
          </div>
        </ScrollArea>
      </motion.div>
    );
  };

  const renderCurriculumSelectionStep = () => {
    if (!selectedParentSubject) return null;

    const curriculumLevels = getCurriculumLevelsForSubject(selectedParentSubject.id);

    return (
      <motion.div
        key="curriculum-selection"
        animate="animate"
        className="absolute inset-0 mx-auto w-full max-w-3xl px-4"
        custom={direction}
        exit="exit"
        initial="initial"
        variants={pageTransitionVariants}
      >
        <motion.div animate="animate" className="mb-6" initial="initial" variants={headerVariants}>
          <div className={"flex w-full items-center justify-start"}>
            <Button
              className="mb-4 h-9 rounded-full"
              size="sm"
              variant="outline"
              onClick={navigateBack}
            >
              <ChevronLeft className="mr-1" height={16} width={16} />
              {t("profile:selectSubjects.subjects")}
            </Button>
          </div>

          <div className="text-center">
            <h3 className="mb-1 text-xl font-medium">{selectedParentSubject.name}</h3>
            <p className="text-muted-foreground border-accent-foreground/10 bg-foreground/50 rounded-2xl border p-2 backdrop-blur-xl">
              {t("profile:selectSubjects.selectCurriculumLevels", {
                subject: selectedParentSubject.name,
              })}
            </p>
          </div>
        </motion.div>

        <ScrollArea className="-mx-4 h-[50dvh] px-4 pb-6">
          <div className="grid gap-2 pt-2">
            {curriculumLevels.map((level, index) => (
              <motion.div
                key={level.id}
                animate="visible"
                custom={index}
                initial="hidden"
                variants={listItemVariants}
              >
                <Button
                  className={cn(
                    "mx-auto h-auto w-full max-w-[80vw] justify-start px-4 py-3 text-start",
                    selectedParentCurriculumLevels.includes(level.id)
                      ? "bg-side-accent text-accent-foreground hover:bg-side-accent/90"
                      : "bg-foreground text-muted-foreground hover:bg-muted/50",
                  )}
                  variant="outline"
                  onClick={() => handleCurriculumLevelToggle(level.id, true)}
                >
                  <div className="flex w-full items-center justify-between">
                    <span className="truncate">{level.name}</span>
                    {selectedParentCurriculumLevels.includes(level.id) && (
                      <motion.div
                        animate={{ scale: 1, opacity: 1 }}
                        initial={{ scale: 0, opacity: 0 }}
                        transition={{ duration: 0.2, type: "spring", stiffness: 500 }}
                      >
                        <Check
                          className="text-accent-foreground ml-2 shrink-0"
                          height={16}
                          width={16}
                        />
                      </motion.div>
                    )}
                  </div>
                </Button>
              </motion.div>
            ))}
          </div>
        </ScrollArea>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 flex justify-end max-lg:mt-2"
          initial={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Button className="mr-2" variant="outline" onClick={navigateBack}>
            {t("common:cancel")}
          </Button>
          <Button
            disabled={selectedParentCurriculumLevels.length === 0}
            variant="foreground"
            onClick={handleCurriculumLevelConfirm}
          >
            {t("common:confirm")}
          </Button>
        </motion.div>
      </motion.div>
    );
  };

  const renderChildCurriculumSelectionStep = () => {
    if (!selectedChildSubject) return null;

    // Child subjects inherit curriculum levels from their parent
    const curriculumLevels = getCurriculumLevelsForChildSubject();

    return (
      <motion.div
        key="child-curriculum-selection"
        animate="animate"
        className="absolute inset-0 mx-auto w-full max-w-3xl px-4"
        custom={direction}
        exit="exit"
        initial="initial"
        variants={pageTransitionVariants}
      >
        <motion.div animate="animate" className="mb-6" initial="initial" variants={headerVariants}>
          <Button
            className="mb-4 h-9 rounded-full"
            size="sm"
            variant="outline"
            onClick={navigateBack}
          >
            <ChevronLeft className="mr-1" height={16} width={16} />
            {t("profile:selectSubjects.subjects")}
          </Button>

          <div className="text-center">
            <h3 className="mb-1 text-xl font-medium">{selectedChildSubject.name}</h3>
            <p className="text-muted-foreground">
              {t("profile:selectSubjects.selectCurriculumLevels", {
                subject: selectedChildSubject.name,
              })}
            </p>
          </div>
        </motion.div>

        <ScrollArea className="-mx-4 h-[50dvh] px-4 pb-6">
          <div className="grid gap-2 pt-2">
            {curriculumLevels.map((level, index) => (
              <motion.div
                key={level.id}
                animate="visible"
                custom={index}
                initial="hidden"
                variants={listItemVariants}
              >
                <Button
                  className={cn(
                    "mx-auto h-auto w-full max-w-[80vw] justify-start px-4 py-3 text-start",
                    selectedChildCurriculumLevels.includes(level.id)
                      ? "bg-side-accent text-accent-foreground hover:bg-side-accent/90"
                      : "bg-foreground text-muted-foreground hover:bg-muted/50",
                  )}
                  variant="outline"
                  onClick={() => handleCurriculumLevelToggle(level.id, false)}
                >
                  <div className="flex w-full items-center justify-between">
                    <span className="truncate">{level.name}</span>
                    {selectedChildCurriculumLevels.includes(level.id) && (
                      <motion.div
                        animate={{ scale: 1, opacity: 1 }}
                        initial={{ scale: 0, opacity: 0 }}
                        transition={{ duration: 0.2, type: "spring", stiffness: 500 }}
                      >
                        <Check
                          className="text-accent-foreground ml-2 shrink-0"
                          height={16}
                          width={16}
                        />
                      </motion.div>
                    )}
                  </div>
                </Button>
              </motion.div>
            ))}
          </div>
        </ScrollArea>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 flex flex-wrap justify-between gap-2"
          initial={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Button
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            variant="outline"
            onClick={() => {
              const existingIndex = subjectsForm.findIndex((s) => s.id === selectedChildSubject.id);

              if (existingIndex !== -1) {
                remove(existingIndex);
              }
              setDirection(-1);
              setCurrentStep(SelectionStep.CHILD_SUBJECTS);
            }}
          >
            {t("common:remove")}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={navigateBack}>
              {t("common:cancel")}
            </Button>
            <Button
              disabled={selectedChildCurriculumLevels.length === 0}
              variant="foreground"
              onClick={handleChildCurriculumLevelConfirm}
            >
              {t("common:confirm")}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <motion.div
      animate="visible"
      className="relative h-[75dvh] w-full md:h-[80dvh]"
      initial="hidden"
      variants={containerVariants}
    >
      <FormField
        control={control}
        name="subjects"
        render={() => (
          <FormItem className="h-full">
            <FormControl>
              <div className="relative h-full">
                <AnimatePresence custom={direction} mode="wait">
                  {currentStep === SelectionStep.PARENT_SUBJECTS && renderParentSubjectsStep()}
                  {currentStep === SelectionStep.CHILD_SUBJECTS && renderChildSubjectsStep()}
                  {currentStep === SelectionStep.CURRICULUM_SELECTION &&
                    renderCurriculumSelectionStep()}
                  {currentStep === SelectionStep.CHILD_CURRICULUM_SELECTION &&
                    renderChildCurriculumSelectionStep()}
                </AnimatePresence>
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    </motion.div>
  );
}
